/**
 * Module valuation-income defines income valuation parameters and results, including
 * management costs, tenant and structure values, deductions, aggregates, and KPIs.
 *
 * Tenant contracts and the shared tenant aggregate schema are defined in
 * valuation-tenants.ts.
 */
import { z } from 'zod';
import { ISODateSchema, UUIDSchema } from './common';
import { AreaValueSchema, PercentageSchema } from './measurements';
import { MonetaryValueSchema, SquareMetreMoneyPerAreaValueSchema } from './money';
import { RentSummarySchema } from './tenant-summary';
import { RoundingStrategySchema } from './valuation-primitives';
import { ValuationSensitivityMatrixSchema } from './valuation-sensitivity';
import { ManagementCostParameterSchema, PerConstructionCostManagementCostParameterSchema, RentSchema, RentalAgreementTypeSchema, TenantAggregateSchema, } from './valuation-tenants';
const ManagementCostsSchema = z.object({
    administrative_cost: ManagementCostParameterSchema.optional(),
    maintenance_cost: ManagementCostParameterSchema.optional(),
    operating_cost: ManagementCostParameterSchema.optional(),
    loss_of_rent_risk: ManagementCostParameterSchema.optional(),
});
const MortgageManagementCostsSchema = ManagementCostsSchema.extend({
    modernization_risk: PerConstructionCostManagementCostParameterSchema.optional(),
});
const IncomeTenantParameterSchema = z.object({
    tenant_uuid: UUIDSchema,
    discount_rate: z.number(),
    capitalization_rate: z.number(),
    reasonable_rent: RentSchema.optional(),
    sustainable_rent: RentSchema.optional(),
    management_costs: ManagementCostsSchema,
    mortgage_management_costs: MortgageManagementCostsSchema,
});
const RentInstallmentWithDeltaSchema = z.object({
    monthly_rent_per_unit: MonetaryValueSchema,
    monthly_rent_per_sqm: MonetaryValueSchema.optional(),
    yearly_rent: MonetaryValueSchema,
    delta_monthly_rent_per_unit: MonetaryValueSchema,
    delta_monthly_rent_per_sqm: MonetaryValueSchema.optional(),
    delta_percentage: z.number().optional(),
});
const ReadIncomeTenantParameterSchema = IncomeTenantParameterSchema.extend({
    reasonable_rent_details: RentInstallmentWithDeltaSchema,
});
export const TenantIncomeValuationParametersSchema = z.object({
    tenant_parameters: ReadIncomeTenantParameterSchema.array(),
});
const IncomeValuationStructureParameterSchema = z.object({
    user_defined_construction_cost: MonetaryValueSchema.optional(),
});
const IncomeValuationParameterSetSchema = z.object({
    adjustment_factor: z.number(),
    user_defined_value: MonetaryValueSchema.optional(),
    demolition_cost: MonetaryValueSchema.optional(),
    discounted_land_value_minus_demolition_cost_duration: z.number().optional(),
    discounted_land_value_minus_demolition_cost_discount_rate: z.number().optional(),
    include_sensitivity_matrix: z.boolean(),
});
const IncomeValuationMortgageParameterSetSchema = IncomeValuationParameterSetSchema.extend({
    structures: IncomeValuationStructureParameterSchema.array(),
});
const IncomeDescriptionsSchema = z.object({
    contractual_rent_and_vacancy: z.string().optional(),
    main_lease_description: z.string().optional(),
    gross_rent: z.string().optional(),
    sustainable_rent: z.string().optional(),
    administrative_cost: z.string().optional(),
    maintenance_cost: z.string().optional(),
    operating_cost: z.string().optional(),
    loss_of_rent_risk: z.string().optional(),
    modernization_risk: z.string().optional(),
    adjusted_discount_rate: z.string().optional(),
    capitalization_rate: z.string().optional(),
    multiplier: z.string().optional(),
    market_adjustment: z.string().optional(),
    special_property_feature: z.string().optional(),
});
const IncomeValuationParametersSchema = z.object({
    market: IncomeValuationParameterSetSchema,
    mortgage: IncomeValuationMortgageParameterSetSchema,
    tenant_parameters: ReadIncomeTenantParameterSchema.array(),
    descriptions: IncomeDescriptionsSchema.optional(),
});
export const ReadIncomeValuationParametersSchema = IncomeValuationParametersSchema;
const IncomeTenantValueSchema = z.object({
    reference_number: z.string().optional(),
    annual_net_income: MonetaryValueSchema,
    annual_gross_income: MonetaryValueSchema,
    return_on_land_value: MonetaryValueSchema,
    income_value_structure: MonetaryValueSchema,
    net_rental_income_structures: MonetaryValueSchema,
    management_costs: MonetaryValueSchema,
    multiplier: z.number(),
    discount_rate: z.number(),
});
const IncomeMortgageTenantValueSchema = IncomeTenantValueSchema.extend({
    modernization_cost: MonetaryValueSchema,
});
const IncomeStructureBaseSchemaShape = {
    value: MonetaryValueSchema,
    net_income_construction: MonetaryValueSchema,
    annual_gross_income: MonetaryValueSchema,
    annual_net_income: MonetaryValueSchema,
    return_on_land_value: MonetaryValueSchema,
    management_costs: MonetaryValueSchema,
    management_costs_percentage: z.number(),
    multiplier: z.number(),
    construction_cost: MonetaryValueSchema,
};
const IncomeStructureValuesSchema = z.object({
    value: MonetaryValueSchema,
    net_income_construction: MonetaryValueSchema,
    annual_gross_income: MonetaryValueSchema,
    annual_net_income: MonetaryValueSchema,
    return_on_land_value: MonetaryValueSchema,
    management_costs: MonetaryValueSchema,
    management_costs_percentage: z.number(),
    multiplier: z.number(),
    construction_cost: MonetaryValueSchema,
    tenant_values: IncomeTenantValueSchema.array(),
});
const IncomeMortgageStructureValuesSchema = z.object({
    ...IncomeStructureBaseSchemaShape,
    tenant_values: IncomeMortgageTenantValueSchema.array(),
});
const IncomeValuationKpisSchema = z.object({
    gross_yield: z.number().optional(),
    gross_initial_yield: z.number().optional(),
    cap_rate: z.number().optional(),
    net_yield: z.number().optional(),
    net_initial_yield: z.number().optional(),
    value_per_rentable_area: SquareMetreMoneyPerAreaValueSchema.optional(),
    gross_multiplier: z.number().optional(),
    net_multiplier: z.number().optional(),
    gross_initial_multiplier: z.number().optional(),
    asset_value: MonetaryValueSchema.optional(),
    gross_yield_before_special_property_features: z.number().optional(),
    gross_initial_yield_before_special_property_features: z.number().optional(),
    cap_rate_before_special_property_features: z.number().optional(),
    net_yield_before_special_property_features: z.number().optional(),
    net_initial_yield_before_special_property_features: z.number().optional(),
    value_per_rentable_area_before_special_property_features: SquareMetreMoneyPerAreaValueSchema.optional(),
    gross_multiplier_before_special_property_features: z.number().optional(),
    net_multiplier_before_special_property_features: z.number().optional(),
    gross_initial_multiplier_before_special_property_features: z.number().optional(),
    asset_value_before_special_property_features: MonetaryValueSchema.optional(),
});
const PerTenantOverUnderRentSchema = z.object({
    apply: z.boolean(),
    tenant_uuid: UUIDSchema,
    rental_agreement_type: RentalAgreementTypeSchema,
    reference_number: z.string().optional(),
    tenant_name: z.string().optional(),
    annual_actual_income: MonetaryValueSchema,
    annual_gross_income: MonetaryValueSchema,
    delta: MonetaryValueSchema,
    loss_of_rent_risk_adjusted_delta: MonetaryValueSchema,
    discount_rate: PercentageSchema,
    adjusted_discount_rate: PercentageSchema.optional(),
    present_value_factor: z.number(),
    discount_factor: z.number(),
    relevant_start_date: ISODateSchema.optional(),
    cashflow_start_date: ISODateSchema.optional(),
    cashflow_end_date: ISODateSchema.optional(),
    remaining_years: z.number().optional(),
    result: MonetaryValueSchema,
    category: z.string().optional(),
});
const VacancyAndRentFreeTimeTenantBaseSchemaShape = {
    apply: z.boolean(),
    tenant_uuid: UUIDSchema,
    reference_number: z.string().optional(),
    tenant_name: z.string().optional(),
    area: AreaValueSchema.optional(),
    annual_gross_income: MonetaryValueSchema,
    loss_of_rent_risk_adjusted_annual_gross_income: MonetaryValueSchema,
    remaining_operating_cost_value: MonetaryValueSchema,
    rental_loss_with_remaining_operating_costs: MonetaryValueSchema,
    discount_rate: PercentageSchema,
    adjusted_discount_rate: PercentageSchema.optional(),
    present_value_factor: z.number(),
    relevant_start_date: ISODateSchema,
    period_start_date: ISODateSchema.optional(),
    period_end_date: ISODateSchema,
    remaining_years: z.number().optional(),
    value_before_discounting: MonetaryValueSchema,
    discount_factor: z.number(),
    result: MonetaryValueSchema,
    category: z.string().optional(),
};
const PerTenantRentFreeTimeDeductionSchema = z.object(VacancyAndRentFreeTimeTenantBaseSchemaShape);
const PerTenantVacancyDeductionSchema = z.object({
    ...VacancyAndRentFreeTimeTenantBaseSchemaShape,
    present_value_of_rent_loss: MonetaryValueSchema,
    fitout_cost_value: MonetaryValueSchema,
    marketing_cost_value: MonetaryValueSchema,
});
const OverUnderRentResultSchema = z.object({
    tenant_results: PerTenantOverUnderRentSchema.array(),
    value_non_rounded: MonetaryValueSchema,
    value_rounded: MonetaryValueSchema,
    rounding_strategy: RoundingStrategySchema,
    description: z.string().optional(),
});
const VacancyDeductionResultSchema = z.object({
    tenant_results: PerTenantVacancyDeductionSchema.array(),
    value_non_rounded: MonetaryValueSchema,
    value_rounded: MonetaryValueSchema,
    rounding_strategy: RoundingStrategySchema,
    description: z.string().optional(),
});
const RentFreeTimeDeductionResultSchema = z.object({
    tenant_results: PerTenantRentFreeTimeDeductionSchema.array(),
    value_non_rounded: MonetaryValueSchema,
    value_rounded: MonetaryValueSchema,
    rounding_strategy: RoundingStrategySchema,
    description: z.string().optional(),
});
const ManagementCostValueSchema = z.object({
    monetary_value: MonetaryValueSchema,
    percentage: PercentageSchema.optional(),
    value_per_area: SquareMetreMoneyPerAreaValueSchema.optional(),
    value_per_unit: MonetaryValueSchema.optional(),
});
const IncomeManagementCostsSchema = z.object({
    administrative_cost: ManagementCostValueSchema,
    maintenance_cost: ManagementCostValueSchema,
    loss_of_rent_risk_cost: ManagementCostValueSchema,
    operating_cost: ManagementCostValueSchema,
    modernization_risk: ManagementCostValueSchema,
    total: ManagementCostValueSchema,
});
const IncomeTenantAggregateSchema = TenantAggregateSchema.extend({
    annual_gross_income: RentSummarySchema,
    discount_rates: z.number().array(),
    management_costs: IncomeManagementCostsSchema,
});
const IncomeTenantSummarySchema = z.object({
    tenant_aggregates: IncomeTenantAggregateSchema.array(),
    total: IncomeTenantAggregateSchema,
});
const IncomeValuationBaseSchemaShape = {
    asset_value_non_rounded: MonetaryValueSchema,
    asset_value_rounded: MonetaryValueSchema,
    land_value: MonetaryValueSchema,
    land_value_yielding: MonetaryValueSchema,
    land_value_non_yielding: MonetaryValueSchema,
    market_adjustment_factor: z.number(),
    net_rental_income: MonetaryValueSchema,
    gross_rental_income: MonetaryValueSchema,
    return_on_land_value: MonetaryValueSchema,
    value_construction: MonetaryValueSchema,
    preliminary_asset_value: MonetaryValueSchema,
    market_adjusted_preliminary_asset_value: MonetaryValueSchema,
    rounding_strategy: RoundingStrategySchema,
    user_defined_value: MonetaryValueSchema.optional(),
    tenant_summary: IncomeTenantSummarySchema,
    special_property_features_value: MonetaryValueSchema,
    management_costs: MonetaryValueSchema.optional(),
    use_weighted_approach: z.boolean(),
    weighted_discount_rate: z.number(),
    weighted_multiplier: z.number(),
    weighted_residual_life_span: z.number(),
    weighted_return_on_land_value: MonetaryValueSchema,
    weighted_net_income_construction: MonetaryValueSchema,
    gross_yield: z.number().optional(),
    gross_initial_yield: z.number().optional(),
    kpis: IncomeValuationKpisSchema,
    over_under_rent_results: OverUnderRentResultSchema.optional(),
    vacancy_deduction_results: VacancyDeductionResultSchema.optional(),
    rent_free_time_deduction_result: RentFreeTimeDeductionResultSchema.optional(),
};
const IncomeValuationSchema = z.object({
    ...IncomeValuationBaseSchemaShape,
    structures: IncomeStructureValuesSchema.array(),
});
const IncomeMortgageValuationSchema = z.object({
    ...IncomeValuationBaseSchemaShape,
    structures: IncomeMortgageStructureValuesSchema.array(),
    is_management_cost_below_minimum: z.boolean(),
    is_net_income_of_construction_negative: z.boolean(),
    land_value_minus_demolition_cost: MonetaryValueSchema.optional(),
    land_value_minus_demolition_cost_discount_factor: z.number().optional(),
    discounted_land_value_minus_demolition_cost: MonetaryValueSchema.optional(),
});
export const CombinedIncomeValuationSchema = z.object({
    market: IncomeValuationSchema,
    mortgage: IncomeMortgageValuationSchema.optional(),
    sensitivity_matrix: ValuationSensitivityMatrixSchema.optional(),
});
